import type { JSX } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DataTableFilters } from '@/shared/components/DataTableFilters';
import { ResultCount } from '@/shared/components/ResultCount';
import { useTableFilters } from '@/shared/hooks/useTableFilters';
import { VerificationRows } from './VerificationRows';
import { verificationTableColumns } from '../config/verificationTableColumns';
import type { Verification } from '../types/verification.types';
import { useCopyRefereeLink } from '../hooks/useCopyRefereeLink';

export interface VerificationListProps {
  verifications: Verification[];
  /** Whether the query ever resolved. Distinguishes a cached empty list from a failed first load. */
  hasData: boolean;
  isLoading: boolean;
  isError?: boolean;
  onDelete: (id: string) => void;
  onAdd: () => void;
  candidateId: string;
  experienceId: string;
}

function AddButton({ onClick }: { onClick: () => void }): JSX.Element {
  return (
    <Button variant="outline" size="sm" className="mt-2" onClick={onClick}>
      <Plus className="mr-1 h-4 w-4" />
      Add Verification
    </Button>
  );
}

function EmptyState({ onAdd }: { onAdd: () => void }): JSX.Element {
  return (
    <div className="space-y-3">
      <ResultCount count={0} />
      <div className="text-center py-4 text-muted-foreground">
        <p>No verifications yet.</p>
        <AddButton onClick={onAdd} />
      </div>
    </div>
  );
}

export function VerificationList({
  verifications,
  hasData,
  isLoading,
  isError,
  onDelete,
  onAdd,
  candidateId,
  experienceId,
}: VerificationListProps): JSX.Element {
  const { copyRefereeLink } = useCopyRefereeLink({ candidateId, experienceId });
  const { filteredSorted, sort, toggleSort, ...toolbar } = useTableFilters(
    verifications,
    verificationTableColumns
  );

  if (isLoading) {
    return <div className="text-center py-4 text-muted-foreground">Loading verifications...</div>;
  }

  // A failed refetch keeps whatever resolved before, so only report the failure when nothing ever did.
  if (isError && !hasData) {
    return <div className="text-center py-4 text-destructive">Failed to load verifications</div>;
  }

  if (verifications.length === 0) {
    return <EmptyState onAdd={onAdd} />;
  }

  return (
    <div className="space-y-3">
      <ResultCount count={filteredSorted.length} />
      <DataTableFilters
        columns={verificationTableColumns}
        filters={toolbar.filters}
        activeFilterCount={toolbar.activeFilterCount}
        onFilterChange={toolbar.setFilter}
        onClearFilters={toolbar.clearFilters}
        searchTerm={toolbar.searchTerm}
        onSearchChange={toolbar.setSearchTerm}
        title="Verification filters"
      />
      <VerificationRows
        verifications={filteredSorted}
        sort={sort}
        onToggleSort={toggleSort}
        onDelete={onDelete}
        onCopyRefereeLink={copyRefereeLink}
      />
      <AddButton onClick={onAdd} />
    </div>
  );
}
